/**
 * Entity → display metadata mapping for AI search result cards.
 * Maps entity names (as returned by BTNet-Search API in snake_case)
 * to display labels and Blueprint icon components.
 *
 * Icon assignments follow the idealized Bp→Icon mapping (icon-entity-mapping.csv).
 */
import type { ComponentType, ReactNode } from "react";

import {
    IconAllowances,
    IconBids,
    IconBills,
    IconChangeOrders,
    IconClientUpdates,
    IconComments,
    IconCostCodes,
    IconCreditMemos,
    IconDailyLogs,
    IconDocuments,
    IconEstimate,
    IconInternalUsers,
    IconJobs,
    IconLeadOpportunities,
    IconLeadProposals,
    IconLienWaivers,
    IconMessages,
    IconOwnerInvoices,
    IconPeople,
    IconPhotos,
    IconPurchaseOrders,
    IconReceipts,
    IconRfIs,
    IconSchedule,
    IconSelections,
    IconSpecifications,
    IconStamp,
    IconSubsVendors,
    IconSurveys,
    IconTimeClock,
    IconToDos,
    IconVideos,
    IconWarranty,
} from "@btnet/blueprint-icons";
import type { IIconProps } from "@btnet/blueprint-icons";

export interface IEntityMeta {
    label: string;
    icon: ReactNode;
}

/**
 * Search rows need an icon size the icon package lacks, so the icon fills a
 * token-sized box instead.
 */
function entityIcon(EntityIcon: ComponentType<IIconProps>): ReactNode {
    return <EntityIcon size="auto" className="size-6" />;
}

/**
 * Keys are normalized entity names: lowercased with hyphens and underscores stripped.
 * The API returns snake_case (e.g. "daily_log") which normalizes to "dailylog".
 */
const META: Record<string, IEntityMeta> = {
    // ── Jobs & Leads ────────────────────────────────────────────────────
    job: { label: "Job", icon: entityIcon(IconJobs) },
    jobsite: { label: "Job", icon: entityIcon(IconJobs) },
    lead: { label: "Lead", icon: entityIcon(IconLeadOpportunities) },
    leadactivity: { label: "Lead Activity", icon: entityIcon(IconLeadOpportunities) },

    // ── People ──────────────────────────────────────────────────────────
    contact: { label: "Contact", icon: entityIcon(IconPeople) },
    sub: { label: "Sub / Vendor", icon: entityIcon(IconSubsVendors) },
    internaluser: { label: "Internal User", icon: entityIcon(IconInternalUsers) },

    // ── Scheduling ──────────────────────────────────────────────────────
    schedule: { label: "Schedule", icon: entityIcon(IconSchedule) },
    scheduleitem: { label: "Schedule", icon: entityIcon(IconSchedule) },
    phase: { label: "Phase", icon: entityIcon(IconSchedule) },

    // ── Tasks ───────────────────────────────────────────────────────────
    task: { label: "Task", icon: entityIcon(IconToDos) },

    // ── Selections ──────────────────────────────────────────────────────
    selection: { label: "Selection", icon: entityIcon(IconSelections) },
    selectionchoice: { label: "Selection Choice", icon: entityIcon(IconSelections) },
    selectionoption: { label: "Selection", icon: entityIcon(IconSelections) },

    // ── Financial ───────────────────────────────────────────────────────
    purchaseorder: { label: "Purchase Order", icon: entityIcon(IconPurchaseOrders) },
    changeorder: { label: "Change Order", icon: entityIcon(IconChangeOrders) },
    bill: { label: "Bill", icon: entityIcon(IconBills) },
    customerinvoice: { label: "Client Invoice", icon: entityIcon(IconOwnerInvoices) },
    allowance: { label: "Allowance", icon: entityIcon(IconAllowances) },
    costcode: { label: "Cost Code", icon: entityIcon(IconCostCodes) },
    costitem: { label: "Cost Item", icon: entityIcon(IconCostCodes) },
    deposit: { label: "Deposit", icon: entityIcon(IconOwnerInvoices) },
    creditmemo: { label: "Credit Memo", icon: entityIcon(IconCreditMemos) },
    estimate: { label: "Estimate", icon: entityIcon(IconEstimate) },
    lienwaiver: { label: "Lien Waiver", icon: entityIcon(IconLienWaivers) },
    receipt: { label: "Receipt", icon: entityIcon(IconReceipts) },

    // ── Bids ────────────────────────────────────────────────────────────
    bid: { label: "Bid", icon: entityIcon(IconBids) },
    bidpackage: { label: "Bid Package", icon: entityIcon(IconBids) },

    // ── Daily Logs & Warranty ───────────────────────────────────────────
    dailylog: { label: "Daily Log", icon: entityIcon(IconDailyLogs) },
    warranty: { label: "Warranty", icon: entityIcon(IconWarranty) },
    warrantyservice: { label: "Warranty Service", icon: entityIcon(IconWarranty) },

    // ── Media & Documents ───────────────────────────────────────────────
    document: { label: "Document", icon: entityIcon(IconDocuments) },
    photo: { label: "Photo", icon: entityIcon(IconPhotos) },
    video: { label: "Video", icon: entityIcon(IconVideos) },

    // ── Communication ───────────────────────────────────────────────────
    email: { label: "Message", icon: entityIcon(IconMessages) },
    emailmessage: { label: "Message", icon: entityIcon(IconMessages) },
    message: { label: "Message", icon: entityIcon(IconMessages) },
    channel: { label: "Channel", icon: entityIcon(IconMessages) },
    comment: { label: "Comment", icon: entityIcon(IconComments) },
    clientupdate: { label: "Client Update", icon: entityIcon(IconClientUpdates) },

    // ── Proposals ───────────────────────────────────────────────────────
    proposal: { label: "Proposal", icon: entityIcon(IconLeadProposals) },
    jobproposal: { label: "Proposal", icon: entityIcon(IconLeadProposals) },
    leadproposal: { label: "Proposal", icon: entityIcon(IconLeadProposals) },

    // ── RFIs & Submittals ───────────────────────────────────────────────
    rfi: { label: "RFI", icon: entityIcon(IconRfIs) },
    submittal: { label: "Submittal", icon: entityIcon(IconStamp) },
    specification: { label: "Specification", icon: entityIcon(IconSpecifications) },

    // ── Other ───────────────────────────────────────────────────────────
    survey: { label: "Survey", icon: entityIcon(IconSurveys) },
    timeclockentry: { label: "Time Clock Entry", icon: entityIcon(IconTimeClock) },
};

const DEFAULT_META: IEntityMeta = {
    label: "Item",
    icon: entityIcon(IconJobs),
};

/**
 * Normalize an entity name to a META lookup key.
 * The API returns snake_case (e.g. "daily_log"); strip separators to match META keys.
 */
function normalizeKey(entity: string): string {
    return entity.toLowerCase().replace(/[-_]/g, "");
}

/**
 * Stable identifier for a search hit used as the React `key` prop,
 * the `pendingKey` state value, and comparison target on the result card.
 */
function getHitKey(hit: { entity: string; id: string }): string {
    return `${hit.entity}:${hit.id}`;
}

export { getHitKey, normalizeKey };

export function getEntityMeta(entity: string): IEntityMeta {
    const key = normalizeKey(entity);
    if (META[key]) return META[key];

    // Generate a reasonable label from the raw entity name for unmapped entities
    const label = entity
        .toLowerCase()
        .replace(/[-_]/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/\b\w/g, (s) => s.toUpperCase());

    return { ...DEFAULT_META, label };
}
